import {
  SelectionPostMessageTypes
} from 'cadenza/workbook/view/view-content/workbook-post-message-types';

export const EMPTY_SELECTION = { };

/**
 * Post-message communication for selection on the iframe side.
 */
export class SelectionIframePostMessageApi {

  /** @type {import('cadenza/workbook/view-content/workbook-iframe-post-message-api').WorkbookIframePostMessageApi} */
  _workbookIFramePostMessageApi;

  /**
   * @param {import('cadenza/workbook/view-content/workbook-iframe-post-message-api').WorkbookIframePostMessageApi} workbookIFramePostMessageApi
   * @param {object} options - Additional (optional) options.
   */
  constructor (workbookIFramePostMessageApi, options = {}) {
    this._workbookIFramePostMessageApi = workbookIFramePostMessageApi;
  }

  /**
   * Inform the parent frame about a new selection made on the iframe.
   *
   * @param {SelectionRecordsDto} records - The new selection
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  setSelection (records) {
    return this._workbookIFramePostMessageApi.request({
      type: SelectionPostMessageTypes.SET_SELECTION,
      detail: { selection: records }
    });
  }

  /**
   * Inform the parent frame about a new selection made on the iframe as a reverse of the given one.
   *
   * @param {SelectionRecordsDto} records - The selection to set the inverse of
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  invertSelection (records) {
    return this._workbookIFramePostMessageApi.request({
      type: SelectionPostMessageTypes.INVERT_SELECTION,
      detail: { selection: records }
    });
  }

  /**
   * Inform the parent frame about an existing selection on a data view to be removed.
   *
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  cancelSelection () {
    return this.setSelection(EMPTY_SELECTION);
  }

  /**
   * Inform the parent frame that a filter should be created based on the selection.
   *
   * @param {boolean} isSpatialFilter - true if a spatial filter should be created
   * @param {string} specifiedSpatialConditionId - If given, add to this condition. Id not, create a new one.
   * @param {boolean} receiveProcessingUpdates - If <code>true</code> the
   * @param {string} uniqueActionId - ID of the action for requesting updates on long running requests.
   * @return {Promise<boolean|NoEndpointDefinedError>}
   *   A promise reflecting the receiving of the request on parent frame. First argument states the
   *   cancellation of the request by the recipient via event#preventDefault().
   */
  addFilterFromSelection (isSpatialFilter, specifiedSpatialConditionId = null, receiveProcessingUpdates = false, uniqueActionId = null) {
    const detail = { isSpatialFilter, specifiedSpatialConditionId, receiveProcessingUpdates, uniqueActionId };
    return this._workbookIFramePostMessageApi.request({
      type: SelectionPostMessageTypes.ADD_FILTER_FROM_SELECTION,
      detail
    });
  }

  /**
   * Makes a request to the parent frame to get all spatial conditions.
   *
   * @return {Promise<object>} Spatial conditions by ID (see 'workbook.state.js
   * -> getSpatialConditionsForAllViews')
   */
  getSpatialConditionsForAllViews () {
    return new Promise((resolve, reject) => {
      const self = this;
      // This listener will remove itself on invocation
      function listener (event) {
        self._workbookIFramePostMessageApi.removeEventListener(SelectionPostMessageTypes.GET_SPATIAL_CONDITIONS, this);
        if (event.detail.error) {
          reject(new Error(event.detail.error));
        } else {
          resolve(event.detail);
        }
      }
      this._workbookIFramePostMessageApi.addEventListener(SelectionPostMessageTypes.GET_SPATIAL_CONDITIONS, listener);
      this._workbookIFramePostMessageApi.request(SelectionPostMessageTypes.GET_SPATIAL_CONDITIONS, {});
    });
  }

}
